import { Grid, TextField, Box, FormControlLabel, styled, Switch } from '@mui/material';
import React, { useEffect, useState } from 'react';
import toast from "react-hot-toast";
import Skeleton from 'react-loading-skeleton'
import 'react-loading-skeleton/dist/skeleton.css'

export const IOSSwitch = styled((props) => (
    <Switch focusVisibleClassName=".Mui-focusVisible" disableRipple {...props} />
))(({ theme }) => ({
    width: 42,
    height: 26,
    padding: 0,
    '& .MuiSwitch-switchBase': {
        padding: 0,
        margin: 2,
        transitionDuration: '300ms',
        '&.Mui-checked': {
            transform: 'translateX(16px)',
            color: '#fff',
            '& + .MuiSwitch-track': {
                backgroundColor: '#65C466',
                opacity: 1,
                border: 0,
                ...(theme?.applyStyles?.('dark', {
                    backgroundColor: '#2ECA45',
                }) || {}),
            },
            '&.Mui-disabled + .MuiSwitch-track': {
                opacity: 0.5,
            },
        },
        '&.Mui-focusVisible .MuiSwitch-thumb': {
            color: '#33cf4d',
            border: '6px solid #fff',
        },
        '&.Mui-disabled .MuiSwitch-thumb': {
            color: theme.palette.grey[100],
            ...(theme?.applyStyles?.('dark', {
                color: theme.palette.grey[600],
            }) || {}),
        },
        '&.Mui-disabled + .MuiSwitch-track': {
            opacity: 0.7,
            ...(theme?.applyStyles?.('dark', {
                opacity: 0.3,
            }) || {}),
        },
    },
    '& .MuiSwitch-thumb': {
        boxSizing: 'border-box',
        width: 22,
        height: 22,
    },
    '& .MuiSwitch-track': {
        borderRadius: 26 / 2,
        backgroundColor: '#E9E9EA',
        opacity: 1,
        transition: theme.transitions.create(['background-color'], {
            duration: 500,
        }),
        ...(theme?.applyStyles?.('dark', {
            backgroundColor: '#39393D',
        }) || {}),
    },
}));

const fieldLabels = {
    retailPrice: 'Retail Price ($)',
    stock_quantity: 'Stock Quantity',
    dimensions: 'Dimensions (L×W×H)',
    is_default: 'Is Default?',
    discount: 'Discount (%)',
    weight: "Weight",
    color:"Color"
};

const AttributeForm = ({ isPriceDisplay, formState, setFormState, isMultipleSizes }) => {
    const [isSizeDisplay, setIsSizeDisplay] = useState(true)

    const handleInputChange = (attrIndex, key, value, subIndex = null) => {
        const updated = [...formState];

        if (subIndex === null) {
            updated[attrIndex][key] = ['basePrice', 'retailPrice', 'stock_quantity'].includes(key)
                ? Number(value)
                : value;
        } else {
            if (key === 'color') {
                value = value.split(',').map(c => c.trim());
            } else if (key === 'weight') {
                value = value.split(',').map(c => c.trim());
            }
            else if (['basePrice', 'retailPrice', 'stock_quantity'].includes(key)) {
                value = Number(value);
            }

            updated[attrIndex].size_attribute[subIndex][key] = value;
        }

        setFormState(updated);
    };

    const addAttribute = () => {
        setFormState([
            ...formState,
            {
                size: '',
                size_attribute: [
                    {
                        basePrice: '',
                        retailPrice: '',
                        stock_quantity: '',
                        weight: [],
                        dimensions: '',
                        is_default: false,
                        discount: '',
                        color: [] // Initialized as array
                    }
                ]
            }
        ]);
    };

    const addSizeAttributeOption = (attrIndex) => {
        const sizeAttributes = formState[attrIndex]?.size_attribute;
        const lastOption = sizeAttributes[sizeAttributes.length - 1];

        if (!lastOption.basePrice || !lastOption.retailPrice || !lastOption.stock_quantity) {
            toast.error("Please fill all required fields before adding a new option.");
            return;
        }

        const updated = [...formState];
        updated[attrIndex].size_attribute.push({
            basePrice: '',
            retailPrice: '',
            stock_quantity: '',
            weight: [],
            dimensions: '',
            is_default: false,
            discount: '',
            color: []
        });
        setFormState(updated);
    };

    const removeAttribute = (index) => {
        const updated = [...formState];
        updated.splice(index, 1);
        setFormState(updated);
    };

    const removePackOption = (attrIndex, subIndex) => {
        const updated = [...formState];
        updated[attrIndex].size_attribute.splice(subIndex, 1);
        setFormState(updated);
    };

    const [isChecked, setIsChecked] = useState(true);


    return (
        <Box sx={{
            margin: '0 0 30px',
            padding: "30px",
            background: "#fff",
            "& h3": { marginBottom: "10px" },
        }}>
            <div className='flex gap-5'>
                <label className="block text-sm font-medium text-gray-700 mt-3">
                    Do You Want To Add Size
                </label>
                <FormControlLabel
                    control={
                        <IOSSwitch
                            sx={{ m: 1 }}
                            id="isSizeDisplay"
                            name="isSizeDisplay"
                            value={isSizeDisplay}
                            onChange={() => setIsSizeDisplay(prev => !prev)}
                            checked={isSizeDisplay}
                        />
                    }
                />
            </div>

            <h3>Output Table:</h3>

            {formState.length > 1 && formState.map((attr, i) => (
                <div key={i} style={{ marginBottom: '2rem' }}>
                    <h4>Size: {attr.size || `#${i + 1}`}</h4>
                    <table border="1" cellPadding="6" cellSpacing="0" style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead style={{ background: '#f0f0f0' }}>
                            <tr>
                                <th>Retail Price</th>
                                <th>Stock Qty</th>
                                <th>Weight</th>
                                <th>Dimensions</th>
                                <th>Discount</th>
                                <th>Color(s)</th>
                                <th>Is Default</th>
                            </tr>
                        </thead>
                        <tbody>
                            {attr.size_attribute.map((subAttr, j) => (
                                <tr key={j}>
                                    <td>{subAttr.retailPrice}</td>
                                    <td>{subAttr.stock_quantity}</td>
                                    <td>{Array.isArray(subAttr.weight) ? subAttr.weight.join(', ') : subAttr.weight || ''}</td>
                                    <td>{subAttr.dimensions}</td>
                                    <td>{subAttr.discount}</td>
                                    <td>{Array.isArray(subAttr.color) ? subAttr.color.join(', ') : subAttr.color || ''}</td>
                                    <td>{subAttr.is_default ? 'Yes' : 'No'}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            ))}

            <form>
                {formState.map((attr, i) => (
                    <div key={i} style={{ border: '1px solid #ccc', padding: '1rem', marginBottom: '2rem' }}>
                        {
                            isSizeDisplay && (
                                <div>
                                    <label><b>Size:</b></label>
                                    <Grid item xs={12} sx={{ paddingLeft: '0 !important', marginBottom: "10px" }}>
                                        <TextField
                                            fullWidth
                                            required
                                            name="size"
                                            onChange={(e) => handleInputChange(i, 'size', e.target.value)}
                                            value={attr.size}
                                            placeholder="Enter product size"
                                        />

                                    </Grid>
                                </div>
                            )
                        }

                        {formState.length > 1 && (
                            <button
                                type="button"
                                onClick={() => removeAttribute(i)}
                                style={{ marginLeft: '1rem', background: '#ff4d4d', color: '#fff', padding: '0.3rem 0.8rem' }}
                            >
                                Remove Size
                            </button>
                        )}

                        {/* <h4 className='mb-3'>Pack Options for Size: {attr.size || `#${i + 1}`}</h4> */}

                        {attr.size_attribute.map((subAttr, j) => (
                            <div key={j} style={{ marginBottom: '1rem', borderLeft: '3px solid #999', paddingLeft: '1rem' }}
                                className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6 p-8 bg-white">
                                {Object.entries(subAttr).map(([key, val]) => (

                                    fieldLabels[key] && (
                                        <div key={key}
                                        // style={{ marginBottom: '0.3rem' }} 
                                        >
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                {fieldLabels[key]}:
                                            </label>
                                            {key === 'is_default' ? (
                                                <input
                                                    type="checkbox"
                                                    checked={val}
                                                    onChange={(e) => handleInputChange(i, key, e.target.checked, j)}
                                                    // style={{ marginLeft: '0.5rem' }}
                                                    className="border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                                                />
                                            ) : (
                                                <input
                                                    type={['basePrice', 'retailPrice', 'stock_quantity'].includes(key) ? 'text' : 'text'}
                                                    value={Array.isArray(val) ? val.join(', ') : val}
                                                    onChange={(e) => handleInputChange(i, key, e.target.value, j)}
                                                    // style={{ marginLeft: '0.5rem', width: '200px', borderBottom: "1px solid #000" }}
                                                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                                                />
                                            )}
                                        </div>
                                    )
                                ))}

                                {attr.size_attribute.length > 1 && (
                                    <button
                                        type="button"
                                        onClick={() => removePackOption(i, j)}
                                        style={{ background: '#ff8080', color: '#fff', marginTop: '0.5rem' }}
                                    >
                                        Remove Pack Option
                                    </button>
                                )}
                            </div>
                        ))}

                        {/* <button
                            type="button"
                            onClick={() => addSizeAttributeOption(i)}
                            style={{ marginBottom: '1rem', background: '#4CAF50', color: '#fff', padding: '0.3rem 0.8rem' }}
                        >
                            + Add Pack Option
                        </button> */}
                    </div>
                ))}

                {isMultipleSizes && isSizeDisplay && (
                    <button
                        type="button"
                        onClick={addAttribute}
                        style={{ background: '#007bff', color: '#fff', padding: '0.4rem 1rem' }}
                    >
                        + Add Size
                    </button>
                )}
            </form>
        </Box>
    );
};

export default AttributeForm;
